import { useEffect, useState } from 'react';

import styles from './BooksList.module.scss';

import Spinner from '@/dls/Spinner/Spinner';

import type { Book } from './types';

type BooksListProps = {
  /** CDN endpoint that returns an array of books as JSON. */
  booksEndpoint: string;
  /**
   * Called when a book is clicked.
   * If not provided, the component will not render any click affordance beyond the title.
   */
  onBookClick?: (book: Book) => void;
  /** e.g. prefetch route or warm PDF viewer chunk while the user hovers a card. */
  onBookEnter?: (book: Book) => void;
  /** Optional heading to show above the list (e.g. 'Books', 'Articles'). */
  heading?: string;
};

type BooksResponse = Book[];

const BooksList: React.FC<BooksListProps> = ({
  booksEndpoint,
  onBookClick,
  onBookEnter,
  heading,
}) => {
  const [books, setBooks] = useState<Book[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!booksEndpoint) return;

    let isMounted = true;

    const fetchBooks = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await fetch(booksEndpoint);
        if (!res.ok) {
          throw new Error(`Request failed with status ${res.status}`);
        }
        const data = (await res.json()) as BooksResponse;
        if (!isMounted) return;
        setBooks(data);
      } catch (e) {
        if (!isMounted) return;
        setError('Could not load books from the server.');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    void fetchBooks();

    return () => {
      isMounted = false;
    };
  }, [booksEndpoint]);

  if (!booksEndpoint) {
    return (
      <div className={styles.container}>
        <p className={styles.message}>
          Books endpoint is not configured. Please set NEXT_PUBLIC_BOOKS_CDN_URL.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {heading ? <h1 className={styles.heading}>{heading}</h1> : null}

      {isLoading ? (
        <div className={styles.centered}>
          <Spinner />
        </div>
      ) : null}

      {error ? <p className={styles.error}>{error}</p> : null}

      {!isLoading && !error && books && books.length === 0 ? (
        <p className={styles.message}>No books available.</p>
      ) : null}

      {!isLoading && !error && books && books.length > 0 ? (
        <ul className={styles.grid}>
          {books.map((book) => {
            const handleKeyDown = (e: React.KeyboardEvent<HTMLLIElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onBookClick?.(book);
              }
            };

            return (
              <li
                key={book.id}
                className={styles.card}
                onClick={onBookClick ? () => onBookClick(book) : undefined}
                onMouseEnter={onBookEnter ? () => onBookEnter(book) : undefined}
                role={onBookClick ? 'button' : undefined}
                tabIndex={onBookClick ? 0 : undefined}
                onKeyDown={onBookClick ? handleKeyDown : undefined}
              >
              {book.thumbnail ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={book.thumbnail}
                  alt={book.title}
                  className={styles.cover}
                />
              ) : null}
              <div className={styles.cardBody}>
                <h2 className={styles.title}>{book.title}</h2>
                {/* {book.description ? (
                  <p className={styles.description}>{book.description}</p>
                ) : null} */}
              </div>
            </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default BooksList;

